import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  complianceItems,
  exportCountries,
  logisticsSteps,
  topMarkets,
} from "@/mocks/globalReach";

const eyebrow = "text-[14px] tracking-[0.25em] uppercase text-primary-600 mb-3";
const h2 =
  "font-heading text-3xl md:text-5xl leading-tight text-foreground-950 mb-5";
const h2Compliance =
  "font-heading text-3xl md:text-[2.6875rem] leading-tight text-foreground-950 mb-5";
const btnOutline =
  "inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-foreground-900 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-colors";
const btnGradient =
  "group inline-flex rounded-full bg-gradient-to-br from-primary-300 via-primary-500 to-accent-400 p-px whitespace-nowrap cursor-pointer";
const btnGradientInner =
  "inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 text-foreground-950 text-xs tracking-widest uppercase transition-colors group-hover:bg-gradient-to-br group-hover:from-primary-500 group-hover:via-primary-600 group-hover:to-accent-500 group-hover:text-background-50";

type Stat = { value: string; label: string };
type Labeled = { title: string; desc: string };

export function GlobalReachHero() {
  const { t } = useTranslation();
  return (
    <section className="relative h-[600px] md:h-[660px] lg:h-[720px] w-full overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20hero%20banner%20photograph%20of%20a%20large%20cargo%20container%20ship%20loaded%20with%20colorful%20stacked%20containers%20docked%20at%20a%20modern%20seaport%2C%20tall%20gantry%20cranes%20on%20the%20left%2C%20calm%20reflective%20harbour%20water%2C%20bright%20clear%20sky%20with%20soft%20white%20clouds%2C%20faint%20glowing%20golden%20world%20map%20trade%20route%20lines%20arcing%20across%20the%20right%20side%2C%20airy%20luminous%20atmosphere%2C%20crisp%20editorial%20photography%2C%20generous%20open%20space%20for%20text%20on%20the%20left&width=1920&height=820&seq=lam-reach-hero-01&orientation=landscape"
        alt="Container ship carrying Vietnamese rice to global markets"
        title="Global reach of EverRice rice exports"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/75 via-background-50/25 to-transparent" />
      <div className="relative h-full flex flex-col page-gutter">
        <div className="flex-1 flex items-center">
          <div className="max-w-2xl fade-up">
            <div className="flex items-center gap-4 mb-5">
              <p className="font-heading text-base md:text-lg tracking-[0.3em] uppercase text-foreground-700">
                {t("globalReachPage.hero.eyebrow")}
              </p>
              <span className="hidden sm:block h-px w-16 md:w-24 bg-foreground-900/40" />
            </div>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl !leading-[1.3] tracking-[0.02em] text-foreground-950 mb-6">
              {t("globalReachPage.hero.title")}
            </h1>
            <p className="text-sm md:text-base text-foreground-800 leading-relaxed max-w-md mb-8">
              {t("globalReachPage.hero.desc")}
            </p>
            <a href="#markets" className={btnOutline}>
              {t("globalReachPage.hero.cta")}{" "}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ExportMapSection() {
  const { t } = useTranslation();
  const [active, setActive] = useState(exportCountries[0].key);

  return (
    <section
      id="markets"
      className="bg-background-100 page-gutter py-16 md:py-24"
    >
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("globalReachPage.map.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>{t("globalReachPage.map.title")}</h2>
      </div>

      <div className="relative w-full aspect-[16/10] rounded-lg overflow-hidden bg-background-50 border border-background-200">
        <img
          src="https://readdy.ai/api/search-image?query=Flat%20vector%20style%20world%20map%20illustration%20centered%20on%20the%20Pacific%20Ocean%20with%20Asia%20and%20Australia%20in%20the%20middle%2C%20Europe%20and%20Africa%20on%20the%20left%20side%2C%20and%20North%20and%20South%20America%20on%20the%20right%20side%2C%20light%20warm%20gray%20landmasses%20on%20a%20clean%20off%20white%20background%2C%20simple%20minimal%20accurate%20country%20outlines%20and%20soft%20coastlines%2C%20no%20text%20no%20labels%20no%20markers%2C%20even%20soft%20lighting%2C%20clean%20modern%20infographic%20aesthetic&width=1600&height=1000&seq=lam-reach-map-v2&orientation=landscape&nocache=true"
          alt="World map of EverRice rice export markets"
          title="EverRice global rice export markets"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-50/10" />
        {exportCountries.map((c) => {
          const isOn = c.key === active;
          const name = t(`globalReachPage.map.countries.${c.key}`);
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              className="absolute z-10 group cursor-pointer"
              style={{ left: `${c.x}%`, top: `${c.y}%` }}
              aria-label={`${t("globalReachPage.map.labels.viewAria")} - ${name}`}
            >
              <span className="absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6">
                <span
                  className={`w-3 h-3 rounded-full ring-2 ring-background-50 transition-all duration-300 ${isOn ? "bg-accent-500 scale-150" : "bg-primary-500 group-hover:scale-125"}`}
                />
              </span>
              <span
                className={`absolute top-0 -translate-y-1/2 flex items-center gap-1 ${c.side === "right" ? "left-2" : "right-2 flex-row-reverse"}`}
              >
                <span
                  className={`h-px w-4 ${isOn ? "bg-accent-500" : "bg-primary-400"}`}
                />
                <span
                  className={`px-2.5 py-1 rounded-full text-[10px] tracking-[0.15em] uppercase whitespace-nowrap transition-colors ${isOn ? "bg-primary-500 text-background-50" : "bg-background-50/90 text-foreground-800 group-hover:bg-background-50"}`}
                >
                  {name}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function RegionStats() {
  const { t } = useTranslation();
  const stats = t("home.stats", { returnObjects: true }) as unknown as Stat[];
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <p className={eyebrow}>{t("globalReachPage.regionStats.eyebrow")}</p>
          <h2 className={`${h2} mb-0`}>
            {t("globalReachPage.regionStats.title")}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={`rounded-lg p-6 text-center transition-transform duration-300 hover:-translate-y-1 ${i === 1 ? "bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 shadow-lg" : "bg-background-50 border border-background-200 shadow-md"}`}
          >
            <p className="font-heading text-4xl md:text-5xl">{s.value}</p>
            <p
              className={`text-xs tracking-widest uppercase mt-2 ${i === 1 ? "text-background-100" : "text-foreground-800"}`}
            >
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function KeyMarkets() {
  const { t } = useTranslation();
  const items = t("globalReachPage.keyMarkets.items", {
    returnObjects: true,
  }) as unknown as { country: string; region: string; note: string }[];
  return (
    <section className="bg-background-100 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("globalReachPage.keyMarkets.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>
          {t("globalReachPage.keyMarkets.title")}
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((m, i) => (
          <div
            key={m.country}
            className="bg-background-50 rounded-lg border border-background-200 p-6"
          >
            <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mb-4">
              <i
                className={`${topMarkets[i].icon} text-xl text-background-50`}
              />
            </div>
            <h3 className="font-heading text-xl text-foreground-950 mb-1">
              {m.country}
            </h3>
            <p className="text-[11px] tracking-widest uppercase text-primary-600 mb-3">
              {m.region}
            </p>
            <p className="text-xs text-foreground-600 leading-relaxed">
              {m.note}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Logistics() {
  const { t } = useTranslation();
  const items = t("globalReachPage.logistics.items", {
    returnObjects: true,
  }) as unknown as Labeled[];
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("globalReachPage.logistics.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>{t("globalReachPage.logistics.title")}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {items.map((s, i) => (
          <div
            key={s.title}
            className={`rounded-lg p-6 ${i === 2 ? "bg-primary-800 text-background-50" : "bg-background-100 border border-background-200"}`}
          >
            <p
              className={`font-heading text-2xl mb-4 ${i === 2 ? "text-background-50" : "text-primary-600"}`}
            >
              0{i + 1}
            </p>
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${i === 2 ? "bg-background-50/20" : "bg-primary-100"}`}
            >
              <i
                className={`${logisticsSteps[i].icon} text-xl ${i === 2 ? "text-background-50" : "text-primary-600"}`}
              />
            </div>
            <h3 className="text-sm font-medium tracking-wide mb-2">
              {s.title}
            </h3>
            <p
              className={`text-xs leading-relaxed ${i === 2 ? "text-background-200" : "text-foreground-600"}`}
            >
              {s.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Compliance() {
  const { t } = useTranslation();
  const items = t("globalReachPage.compliance.items", {
    returnObjects: true,
  }) as unknown as Labeled[];
  return (
    <section className="bg-background-100 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 items-center">
        <div>
          <p className={eyebrow}>{t("globalReachPage.compliance.eyebrow")}</p>
          <h2 className={h2Compliance}>
            {t("globalReachPage.compliance.title")}
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed max-w-md">
            {t("globalReachPage.compliance.desc")}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {items.map((c, i) => (
            <div
              key={c.title}
              className="flex gap-4 bg-background-50 rounded-lg border border-background-200 p-6"
            >
              <div className="w-12 h-12 shrink-0 rounded-full bg-secondary-100 flex items-center justify-center">
                <i
                  className={`${complianceItems[i].icon} text-xl text-secondary-900`}
                />
              </div>
              <div>
                <h3 className="text-sm font-medium tracking-wide mb-1">
                  {c.title}
                </h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  {c.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function GlobalReachCta() {
  const { t } = useTranslation();
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 rounded-lg bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 px-6 md:px-14 py-10">
        <div>
          <h2 className="font-heading text-2xl md:text-4xl mb-3">
            {t("globalReachPage.cta.title")}
          </h2>
          <p className="text-sm opacity-90 max-w-xl leading-relaxed">
            {t("globalReachPage.cta.desc")}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 text-foreground-950 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
          >
            {t("globalReachPage.cta.ctaContact")}{" "}
            <i className="ri-arrow-right-line" />
          </Link>
          <Link to="/quality" className={btnGradient}>
            <span className={btnGradientInner}>
              {t("globalReachPage.cta.ctaQuality")}{" "}
              <i className="ri-shield-check-line" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
